import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '@/shared/hooks/useApiClient';
import { useAuth } from '@/shared/providers/AuthProvider';
import { createUsersApi, UserUpdatePayload } from '../api/usersApi';
import { createUsersService } from '../services/usersService';

export interface UpdateUserArgs {
  userId: number;
  payload: UserUpdatePayload;
  /**
   * ECOPD-14897: When updating the *current user's own* password, pass their
   * login here so the hook can silently re-authenticate after the PUT succeeds,
   * establishing a fresh k3 session before query invalidation refetches fire.
   * Leave undefined when changing another user's password (no relogin needed).
   */
  reloginAs?: { login: string; password: string };
}

export const useUpdateUser = () => {
  const apiClient = useApiClient();
  const { relogin } = useAuth();
  const usersApi = createUsersApi(apiClient);
  const usersService = createUsersService(usersApi);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, payload }: UpdateUserArgs) =>
      usersService.updateUser(userId, payload),
    onSuccess: async (_, { userId, reloginAs }) => {
      // ECOPD-14897: If this update changed the current user's own password, the
      // k3 backend has invalidated their session token.  Re-authenticate with the
      // new password first so that subsequent query refetches use a valid session.
      if (reloginAs) {
        const ok = await relogin(reloginAs.login, reloginAs.password);
        if (!ok) {
          // relogin failed (bad password round-trip, rate-limit, network error, etc.).
          // The RoR session still exists but the k3 token is stale — every subsequent
          // K3 query will fail with authentication_error.  Redirecting to /login is
          // cleaner than leaving the user in a silently broken state with a success
          // toast already showing.
          window.location.assign('/login');
          return;
        }
      }

      // Invalidate and refetch user-related queries
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['user'] });
      queryClient.invalidateQueries({ queryKey: ['user-details-permissions'] });
      queryClient.invalidateQueries({ queryKey: ['userDetails', userId] });
    },
  });
};